import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import TransportToggle from './TransportToggle';
import PopulateFrom from './PopulateFrom';
import { withPageIcon } from '../lib/uiIcons';
import type { TransportType } from '../types';

interface ConfigPageHeaderProps {
  title: ReactNode;
  /**
   * LOT U2f — clé i18n du titre de la page (`configSound.title`, …). L'icône du
   * niveau 1 vient de la table unique `src/lib/uiIcons.ts`, jamais de la valeur
   * traduite : la page garde « Rider configuration : 🎛️ FOH & MON ».
   */
  iconKey?: string;
  transport: TransportType;
  onTransportChange: (t: TransportType) => void;
  /** Page-level "populate from" handler (fills all fields from a chosen mode). */
  onPopulateAll?: (mode: TransportType) => void;
  /** Freeze the page-level "Populate from" button (lot A2-CAP). */
  disabled?: boolean;
}

/**
 * Shared header for CONFIGURATION pages (P3c — Roadcase).
 *
 * WHAT IT CARRIES (do not drop any of it when restyling):
 *   - the page title, wrapped by the shared i18n key `config.riderConfigTitle`;
 *   - the TRANSPORT MODE selector (van / tourbus / train / plane) — a LOCAL,
 *     per-page mode, never persisted here, so it stays enabled even for a
 *     read-only account;
 *   - the page-level "Populate from" dropdown (fills every field of the page
 *     from another transport mode), FREEZABLE via `disabled` (lot A2-CAP).
 *
 * Presentation now follows the shared `<PageHeader/>` language (`.rc-header`:
 * mono, uppercase, tracked title + actions on the right), and the transport
 * bar rides in `.rc-header-actions` keeping its own class so existing
 * selectors/CSS keep matching. No behaviour, prop or key changed.
 */
export default function ConfigPageHeader({
  title,
  iconKey,
  transport,
  onTransportChange,
  onPopulateAll,
  disabled = false,
}: ConfigPageHeaderProps) {
  const { t } = useTranslation();
  const label = iconKey && typeof title === 'string' ? withPageIcon(iconKey, title) : title;
  return (
    <header className="rc-header rc-header--sticky">
      <div className="rc-header-text">
        <h1 className="rc-header-title">{t('config.riderConfigTitle', { title: label })}</h1>
      </div>
      <div className="rc-header-actions config-transport-bar">
        <TransportToggle value={transport} onChange={onTransportChange} size="sm" />
        {onPopulateAll && (
          <PopulateFrom current={transport} onSelect={onPopulateAll} label={t('config.populateFrom')} disabled={disabled} />
        )}
      </div>
    </header>
  );
}
